import { useEffect, useRef } from "react";

import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import LayoutWrapper from "@/components/Layout/LayoutWrapper";
import IntegrationsHeaderActions from "@/components/PageHeader/actions/pageHeaderActions/IntegrationsHeaderActions/IntegrationsHeaderActions";
import TabFilters from "@/components/TabFilters/TabFilters";
import DisableIntegrationDialog from "@/components/Table/actions/dialogs/Integrations/DisableIntegrationDialog/DisableIntegrationDialog";
import useIntegrationsInlineActions from "@/components/Table/actions/inlineActions/hooks/useIntegrationsInlineActions";
import useTableInlineActions from "@/components/Table/hooks/useTableInlineActions";
import Table from "@/components/Table/Table";
import { USER_PERMISSIONS } from "@/roles/types/enums";
import { ROUTES, TABLE_NAMES } from "@/shared";
import useResponse from "@/shared/hooks/useResponse";
import { useIntegrationsDialogStore } from "@/stores/useIntegrationsDialogStore";
import { useIntegrationsTableStore } from "@/stores/useTableStore";
import { getAccessStatus } from "@/stores/useUserDataStore";

import {
  INTEGRATIONS_PAGE_TABS,
  INTEGRATIONS_PAGE_TABS_PARTIAL
} from "./config";
import { useDisableIntegration } from "./hooks/useDisableIntegration";
import IntegrationsDialogHub from "./IntegrationsDialogHub";

const Integrations = () => {
  const { getData } = useAxiosPrivate();

  const { handleResponse } = useResponse();
  const { fetchData, resetInitialLoading, setIsCheckable, setInlineActions } =
    useIntegrationsTableStore();

  const openEditDialog = useIntegrationsDialogStore(
    state => state.openEditDialog
  );

  const canViewFullTabs = getAccessStatus(
    USER_PERMISSIONS.SeeAnalysisProviders
  );

  const { inlineActions, rowData, isEditDialogOpen } =
    useIntegrationsInlineActions();

  const {
    tableInlineActions,
    allowedToManageData: allowedToManageIntegrations
  } = useTableInlineActions(inlineActions, USER_PERMISSIONS.ManageIntegrations);

  const {
    switchData,
    isDisableDialogLoading,
    isDisableDialogOpen,
    handleDisableClose,
    handleSwitch,
    handleSubmitDisable
  } = useDisableIntegration(ROUTES.Integrations);

  const hasFetched = useRef(false);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      handlePageLoad();
      resetInitialLoading();
    }
  }, []);

  useEffect(() => {
    updateIntegrationsActions();
  }, [tableInlineActions]);

  useEffect(() => {
    if (isEditDialogOpen && rowData) {
      openEditDialog(rowData);
    }
  }, [isEditDialogOpen, rowData]);

  const updateIntegrationsActions = () => {
    setInlineActions(tableInlineActions);
    setIsCheckable(allowedToManageIntegrations);
  };

  const handlePageLoad = async () => {
    updateIntegrationsActions();
    await fetchData(getData, handleResponse);
  };

  const tableTabs = !canViewFullTabs
    ? INTEGRATIONS_PAGE_TABS_PARTIAL
    : INTEGRATIONS_PAGE_TABS;

  return (
    <>
      <LayoutWrapper
        showDetailsSection
        route={ROUTES.Integrations}
        tableName={TABLE_NAMES.Integrations}
      >
        {allowedToManageIntegrations && <IntegrationsHeaderActions />}
      </LayoutWrapper>

      <Table
        route={ROUTES.Integrations}
        hasDetails
        tableRowSwitch={handleSwitch}
        tabFilters={<TabFilters tabs={tableTabs} />}
        hasTabFilters
      />

      <IntegrationsDialogHub />

      {switchData && (
        <DisableIntegrationDialog
          rowData={switchData}
          isLoading={isDisableDialogLoading}
          isOpen={isDisableDialogOpen}
          handleDisableSubmit={handleSubmitDisable}
          handleClose={handleDisableClose}
        />
      )}
    </>
  );
};

export default Integrations;
